// @ts-nocheck
/**
 * Maneuver markers for the native-GL map — a Canvas2D overlay (NOT a Mapbox symbol/circle layer).
 *
 * Uses Canvas2D for the same reason as GlMapBoatLayer: a Mapbox `symbol` layer (the maneuver label) crashes
 * Mapbox incremental placement when a `line` layer (the track) coexists. Canvas2D avoids symbol placement
 * entirely. Draws a dot + single-letter label per maneuver, only for maneuvers within the visible track
 * window (mirrors how the SVG renderers filtered maneuvers out of the windowed track data). Click →
 * applyManeuverPointToSelection; hover grows the dot. Canvas is pointerEvents:none; picking via map events.
 */

import { createEffect, createSignal, onCleanup, onMount } from 'solid-js';
import { defaultChannelsStore } from '../../../../../store/defaultChannelsStore';
import { applyManeuverPointToSelection } from '../../utils/maneuverEventSelection';
import { selectedTime, timeWindow, isUnsetPlaybackTime } from '../../../../../store/playbackStore';
import { selectedRange, cutEvents, isCut } from '../../../../../store/selectionStore';
import { warn } from '../../../../../utils/console';

export interface GlManeuverLayerProps {
  map: any;
  data: any[];
  enabled?: boolean;
  tilesAvailable?: boolean;
  effectivePlaybackTime?: Date | null;
  // Hold the markers back until the track layer reports its first painted frame. Undefined = no gate.
  ready?: boolean;
}

// If the track never reports a paint (no track layer mounted, style never settles, an empty dataset), show
// the markers anyway rather than hiding them forever.
const READY_FALLBACK_MS = 4000;

export default function GlManeuverLayer(props: GlManeuverLayerProps) {
  let canvas: HTMLCanvasElement | null = null;
  let hitTargets: Array<{ x: number; y: number; pt: any }> = [];
  let hoveredIdx = -1;
  const [readyTimedOut, setReadyTimedOut] = createSignal(false);
  const gated = () => props.ready === false && !readyTimedOut();

  const { latName, lngName } = defaultChannelsStore;
  const num = (v: any) => {
    if (v == null) return undefined;
    const n = Number(v);
    return isNaN(n) ? undefined : n;
  };
  const getLat = (d: any) => num(d?.[latName()] ?? d?.Lat ?? d?.lat);
  const getLng = (d: any) => num(d?.[lngName()] ?? d?.Lng ?? d?.lng);
  const getTs = (d: any): number => {
    const t = d?.Datetime ?? d?.timestamp ?? d?.time ?? d?.datetime;
    if (!t) return 0;
    if (t instanceof Date) return t.getTime();
    const p = new Date(t).getTime();
    return isNaN(p) ? 0 : p;
  };
  const isManeuver = (d: any) =>
    d?.Maneuver_type && String(d.Maneuver_type).length === 1 && /[A-Za-z]/.test(String(d.Maneuver_type));

  const container = (): HTMLElement | null => {
    try {
      return props.map?.getCanvasContainer?.() || null;
    } catch {
      return null;
    }
  };
  const sizeCanvas = () => {
    const map = props.map;
    if (!canvas || !map?.getCanvas) return false;
    const mc = map.getCanvas();
    const w = mc.clientWidth || mc.width || 0;
    const h = mc.clientHeight || mc.height || 0;
    if (w <= 0 || h <= 0) return false;
    const dpr = window.devicePixelRatio || 1;
    const cw = Math.round(w * dpr);
    const ch = Math.round(h * dpr);
    if (canvas.width !== cw || canvas.height !== ch) {
      canvas.width = cw;
      canvas.height = ch;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }
    return true;
  };

  // Visible-window filter (time window around playhead, then brush/cut) — matches the SVG maneuver behavior.
  const visibleFilter = () => {
    const brush = selectedRange();
    const cuts = cutEvents();
    const cutOn = isCut() && Array.isArray(cuts) && cuts.length > 0;
    const brushRanges: any[] = Array.isArray(brush) && brush.length > 0 ? brush : [];
    const tw = Number(timeWindow());
    const tf = props.effectivePlaybackTime ?? selectedTime();
    const windowed = tw > 0 && tf && !isUnsetPlaybackTime(tf);
    const winStart = windowed ? tf.getTime() - tw * 60 * 1000 : 0;
    const winEnd = windowed ? tf.getTime() : 0;
    const inAny = (t: number, ranges: any[]) =>
      ranges.some((r) => {
        const s = r.start_time instanceof Date ? r.start_time.getTime() : new Date(r.start_time).getTime();
        const e = r.end_time instanceof Date ? r.end_time.getTime() : new Date(r.end_time).getTime();
        return t >= s && t <= e;
      });
    return (d: any): boolean => {
      const t = getTs(d);
      if (brushRanges.length > 0) {
        if (!inAny(t, brushRanges)) return false;
      } else if (cutOn) {
        if (!inAny(t, cuts.filter((r: any) => typeof r !== 'number'))) return false;
      }
      if (windowed && !(t >= winStart && t <= winEnd)) return false;
      return true;
    };
  };

  const draw = () => {
    const map = props.map;
    if (!canvas || !map) return;
    sizeCanvas();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    hitTargets = [];
    if (props.enabled === false) return;
    // Canvas is already cleared above, so a gated draw leaves the overlay blank until the track paints.
    if (gated()) return;

    const color = props.tilesAvailable !== false ? '#fff' : '#000';
    const visible = visibleFilter();
    const raw = props.data || [];
    let idx = 0;
    for (const d of raw) {
      if (!isManeuver(d) || !visible(d)) continue;
      const lng = getLng(d);
      const lat = getLat(d);
      if (lng === undefined || lat === undefined) continue;
      const p = map.project([lng, lat]);
      if (!p || Number.isNaN(p.x) || Number.isNaN(p.y)) continue;
      const hovered = idx === hoveredIdx;
      const r = hovered ? 6 : 3;
      ctx.save();
      ctx.globalAlpha = hovered ? 1 : 0.8;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.font = `${hovered ? 20 : 14}px sans-serif`;
      ctx.fillStyle = color;
      ctx.fillText(String(d.Maneuver_type), p.x + 5, p.y + 5);
      ctx.restore();
      hitTargets.push({ x: p.x, y: p.y, pt: d });
      idx++;
    }
  };

  // Wide enough to cover the letter and a margin, so the pointer shows before the cursor is on the dot.
  const HIT_R = 28;
  const pick = (e: any) => {
    const pt = e.point;
    let bestI = -1;
    let bestD = HIT_R * HIT_R;
    for (let i = 0; i < hitTargets.length; i++) {
      const h = hitTargets[i];
      const dx = h.x - pt.x;
      const dy = h.y - pt.y;
      const d = dx * dx + dy * dy;
      if (d <= bestD) {
        bestD = d;
        bestI = i;
      }
    }
    return bestI;
  };
  const onClick = (e: any) => {
    const i = pick(e);
    if (i < 0) return;
    const pt = hitTargets[i].pt;
    Promise.resolve(applyManeuverPointToSelection(pt, 'GlManeuverLayer')).catch((err) =>
      warn('GlManeuverLayer: maneuver click error', err)
    );
  };
  const onMove = (e: any) => {
    const i = pick(e);
    if (i !== hoveredIdx) {
      hoveredIdx = i;
      draw();
    }
    // The map's own cursor is the drag hand. Set the pointer after the track handler, which
    // clears the cursor when the mouse leaves the thin track line.
    if (i >= 0) {
      const canvasEl = props.map?.getCanvas?.();
      requestAnimationFrame(() => {
        if (hoveredIdx >= 0 && canvasEl) canvasEl.style.cursor = 'pointer';
      });
    }
  };

  onMount(() => {
    const el = container();
    if (!el) return;
    canvas = document.createElement('canvas');
    canvas.className = 'gl-map-maneuver-overlay';
    Object.assign(canvas.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '999',
    });
    el.appendChild(canvas);
    sizeCanvas();
    const onRender = () => draw();
    const onResize = () => {
      sizeCanvas();
      draw();
    };
    props.map?.on?.('render', onRender);
    props.map?.on?.('move', onRender);
    props.map?.on?.('resize', onResize);
    props.map?.on?.('click', onClick);
    props.map?.on?.('mousemove', onMove);
    window.addEventListener('resize', onResize);
    const readyTimer = window.setTimeout(() => setReadyTimedOut(true), READY_FALLBACK_MS);
    onCleanup(() => {
      window.clearTimeout(readyTimer);
      props.map?.off?.('render', onRender);
      props.map?.off?.('move', onRender);
      props.map?.off?.('resize', onResize);
      props.map?.off?.('click', onClick);
      props.map?.off?.('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
      canvas = null;
    });
  });

  createEffect(() => {
    void props.data;
    void props.enabled;
    void gated();
    void props.tilesAvailable;
    void props.effectivePlaybackTime;
    void selectedTime();
    void timeWindow();
    void selectedRange();
    void cutEvents();
    void isCut();
    draw();
  });

  return null;
}
